/**
 * ChatMarkdown — Markdown-Renderer für Chat-Bubbles (#1180).
 *
 * Nutzt react-markdown + remark-gfm. Code-Blocks via einfaches Theme
 * (kein Shiki, da Shiki +1MB Bundle-Size). Falls Manuel Shiki möchte,
 * können wir später `react-shiki` einbauen.
 */
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check } from 'lucide-react';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="absolute top-2 right-2 p-1 rounded-[6px] bg-white/10 hover:bg-white/20 text-gray-300 text-xs flex items-center gap-1"
      title="Copy"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

const components = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed text-[var(--text-primary)]">{children}</p>
  ),
  h1: ({ children }) => <h1 className="text-lg font-semibold mb-2 text-[var(--text-primary)]">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-semibold mb-2 mt-3 text-[var(--text-primary)]">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mb-1.5 mt-2 text-[var(--text-primary)]">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1 text-[var(--text-primary)]">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1 text-[var(--text-primary)]">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#338edc] hover:underline">{children}</a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[#338edc] pl-3 my-2 text-[var(--text-secondary)] italic">{children}</blockquote>
  ),
  strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-3 border-line" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto border border-line rounded-[8px]">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-surface-input text-xs uppercase text-[var(--text-tertiary)]">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-1.5 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="px-3 py-1.5 border-t border-line">{children}</td>,
  code({ inline, className, children, ...props }) {
    const value = String(children).replace(/\n$/, '');
    if (inline) {
      return (
        <code className="px-1 py-0.5 rounded bg-surface-input text-[13px] font-mono text-[#1a3a5c] dark:text-blue-300 border border-line">
          {value}
        </code>
      );
    }
    const lang = (className || '').replace('language-', '') || 'text';
    return (
      <div className="relative my-2">
        <div className="flex items-center justify-between px-3 py-1.5 bg-[#1f2937] text-gray-300 text-xs rounded-t-[8px]">
          <span className="font-mono">{lang}</span>
        </div>
        <pre className="m-0 p-3 bg-[#0f172a] text-gray-100 rounded-b-[8px] overflow-x-auto text-[13px] font-mono leading-relaxed">
          <code {...props}>{value}</code>
        </pre>
        <CopyButton text={value} />
      </div>
    );
  },
  pre: ({ children }) => <>{children}</>,
};

export default function ChatMarkdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children || ''}
    </ReactMarkdown>
  );
}
